import { useAppDispatch, useAppSelector } from '@hooks/app/redux';
import React from 'react';
import { actions as twilioActions } from '@store/ducks/twilio';
import { useRouteMatch } from 'react-router-dom';
import { showNotification } from '@helpers/notification-helper';
import { useIntl } from 'react-intl';
import Draggable from 'react-draggable';
import TwilioVideo from '../twilio-video/twilio-video';

interface TweelioVideoDraggableProps {
  activeTab: any;
}

const TweelioVideoDraggable: React.FunctionComponent<TweelioVideoDraggableProps> = ({
  activeTab,
}) => {
  const {
    params: { userId },
  } = useRouteMatch<{ userId: string }>();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const reqStatus = useAppSelector((state) => state.twilio.joinTwilioRoomRequestStatus);
  const eVisitCurrentEvent = useAppSelector((state) => state.eVisits.singleEvisitItem);
  const twilioRoomData = useAppSelector((state) => state.twilio.twilioRoomData);
  const twilioErrorMessage = useAppSelector((state) => state.twilio.twilioErrorMessage);

  const [isDraggable, setIsDraggable] = React.useState(false);
  const [x, setX] = React.useState(0);
  const [y, setY] = React.useState(0);
  const isHidden = !twilioRoomData || reqStatus !== 'success' || !eVisitCurrentEvent;

  React.useEffect(() => {
    {
      eVisitCurrentEvent &&
        (eVisitCurrentEvent?.event as any).extraData.clientId === userId &&
        !twilioErrorMessage &&
        dispatch(
          twilioActions.joinTwilioRoomRequest({
            orderId: eVisitCurrentEvent?.order?.id as string,
          }),
        );
    }
  }, [eVisitCurrentEvent, userId, twilioErrorMessage]);

  React.useEffect(() => {
    if (twilioErrorMessage) {
      dispatch(twilioActions.setErrorMessage(''));
      showNotification({
        type: 'ERROR',
        title: formatMessage({
          id: twilioErrorMessage,
        }),
        copy: '',
      });
    }
  }, [twilioErrorMessage, dispatch, formatMessage]);

  React.useEffect(() => {
    if (isHidden) {
      return;
    }

    const handleResize = () => {
      setX(0);
      setY(0);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isHidden]);

  React.useEffect(() => {
    if (activeTab === 'VIDEO') {
      setIsDraggable(false);
    } else {
      setIsDraggable(true);
    }
  }, [activeTab]);

  const handleStop = React.useCallback((event: any, dragElement: any) => {
    setX(dragElement.x);
    setY(dragElement.y);
  }, []);

  if (isHidden) {
    return null;
  }

  return (
    <Draggable position={isDraggable ? { x: x, y: y } : { x: 0, y: 0 }} onStop={handleStop}>
      <div className={activeTab === 'VIDEO' ? 'twilio-video' : 'twilio-video draggable'}>
        <TwilioVideo
          item={eVisitCurrentEvent}
          className={activeTab === 'VIDEO' ? '' : 'draggable'}
        />
      </div>
    </Draggable>
  );
};

export default React.memo(TweelioVideoDraggable);
